import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Plus, Check, X, Edit2, ToggleLeft, ToggleRight, ChevronDown, ChevronRight, Search, FileDown, FilterX } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { api } from '../../../core/apiClient';

interface Row {
  id: number;
  code: string;
  name?: string | null;
  sortOrder: number;
  isActive: number;
}
interface FamilyRow extends Row { name: string }
interface SubfamilyRow extends Row { name: string; compositeCodeId: number }
interface ProductRow extends Row { name: string; subfamilyId: number }
interface ModelRow extends Row { name?: string | null; productId: number }
interface VariantRow extends Row { name?: string | null; modelId: number }

function InlineInput({ value, onChange, placeholder, className = '' }: {
  value: string; onChange: (v: string) => void; placeholder?: string; className?: string;
}) {
  return (
    <Input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={`h-7 text-xs px-2 ${className}`}
    />
  );
}

const emptyForm = () => ({ code: '', name: '' });
type Form = ReturnType<typeof emptyForm>;

/**
 * Arbre complet de "Tipus de Producte": Família (composite_codes) >
 * Subfamília > Producte > Model > Variant — segueix l'estructura del full de
 * càlcul "Generació de codis productes" del client. Els 5 nivells son FK en
 * cascada, tots arrossegables i editables inline. L'antic parell de camps de
 * text lliure familia/subfamilia de composite_codes queda substituït per
 * aquesta jerarquia — ja no s'editen des d'aquí (es poden seguir veient a la
 * pestanya "Tipus de Producte").
 */
export default function AdminProductTree({ showInactive }: { showInactive: boolean }) {
  const [families, setFamilies] = useState<FamilyRow[]>([]);
  const [subfamilies, setSubfamilies] = useState<SubfamilyRow[]>([]);
  const [products, setProducts] = useState<ProductRow[]>([]);
  const [models, setModels] = useState<ModelRow[]>([]);
  const [variants, setVariants] = useState<VariantRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [exporting, setExporting] = useState(false);

  const [expandedFam, setExpandedFam] = useState<Set<number>>(new Set());
  const [expandedSub, setExpandedSub] = useState<Set<number>>(new Set());
  const [expandedProd, setExpandedProd] = useState<Set<number>>(new Set());
  const [expandedModel, setExpandedModel] = useState<Set<number>>(new Set());

  const [famAdding, setFamAdding] = useState(false);
  const [famNewForm, setFamNewForm] = useState(emptyForm());
  const [famEditId, setFamEditId] = useState<number | null>(null);
  const [famEditForm, setFamEditForm] = useState(emptyForm());

  const [subAddingFor, setSubAddingFor] = useState<number | null>(null);
  const [subNewForm, setSubNewForm] = useState(emptyForm());
  const [subEditId, setSubEditId] = useState<number | null>(null);
  const [subEditForm, setSubEditForm] = useState(emptyForm());

  const [prodAddingFor, setProdAddingFor] = useState<number | null>(null);
  const [prodNewForm, setProdNewForm] = useState(emptyForm());
  const [prodEditId, setProdEditId] = useState<number | null>(null);
  const [prodEditForm, setProdEditForm] = useState(emptyForm());

  const [modelAddingFor, setModelAddingFor] = useState<number | null>(null);
  const [modelNewForm, setModelNewForm] = useState(emptyForm());
  const [modelEditId, setModelEditId] = useState<number | null>(null);
  const [modelEditForm, setModelEditForm] = useState(emptyForm());

  const [variantAddingFor, setVariantAddingFor] = useState<number | null>(null);
  const [variantNewForm, setVariantNewForm] = useState(emptyForm());
  const [variantEditId, setVariantEditId] = useState<number | null>(null);
  const [variantEditForm, setVariantEditForm] = useState(emptyForm());

  const draggingFam = useRef<number | null>(null);
  const draggingSub = useRef<SubfamilyRow | null>(null);
  const draggingProd = useRef<ProductRow | null>(null);
  const draggingModel = useRef<ModelRow | null>(null);
  const draggingVariant = useRef<VariantRow | null>(null);
  const [dragOver, setDragOver] = useState<{ level: 'fam' | 'sub' | 'prod' | 'model'; id: number } | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [famRes, subRes, prodRes, modelRes, variantRes] = await Promise.all([
        api.get<{ success: boolean; data: FamilyRow[] }>('/catalog/composite-codes'),
        api.get<{ success: boolean; data: SubfamilyRow[] }>('/catalog/composite-subfamilies'),
        api.get<{ success: boolean; data: ProductRow[] }>('/catalog/composite-products'),
        api.get<{ success: boolean; data: ModelRow[] }>('/catalog/composite-models'),
        api.get<{ success: boolean; data: VariantRow[] }>('/catalog/composite-variants'),
      ]);
      setFamilies(famRes.data ?? []);
      setSubfamilies(subRes.data ?? []);
      setProducts(prodRes.data ?? []);
      setModels(modelRes.data ?? []);
      setVariants(variantRes.data ?? []);
    } catch {
      setError('Error en carregar l\'arbre de productes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const toggle = (set: React.Dispatch<React.SetStateAction<Set<number>>>, id: number) =>
    set((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });

  // ── Helpers genèrics d'alta/edició/baixa/reorder per nivell ───────────────

  const createGeneric = async (path: string, form: Form, parentField: string, parentId: number) => {
    if (!form.code.trim()) return;
    await api.post(`/catalog/${path}`, { ...form, [parentField]: parentId });
    fetchAll();
  };
  const updateGeneric = async (path: string, id: number, form: Form) => {
    await api.put(`/catalog/${path}/${id}`, form);
    fetchAll();
  };
  const toggleActiveGeneric = async (path: string, item: Row) => {
    await api.put(`/catalog/${path}/${item.id}`, { isActive: item.isActive ? 0 : 1 });
    fetchAll();
  };
  const deleteGeneric = async (path: string, item: Row) => {
    try { await api.delete(`/catalog/${path}/${item.id}`); fetchAll(); }
    catch (e: any) { setError(e?.response?.data?.message ?? 'Error en eliminar'); }
  };
  const reorderGeneric = async (path: string, ids: number[]) => {
    try { await api.put(`/catalog/${path}/reorder`, { ids }); }
    catch { setError('Error en desar l\'ordre'); fetchAll(); }
  };

  // Família (composite_codes) — reorder entre elles
  const famDrop = (targetId: number) => {
    const dragId = draggingFam.current; draggingFam.current = null; setDragOver(null);
    if (!dragId || dragId === targetId) return;
    const reordered = [...families];
    const from = reordered.findIndex((f) => f.id === dragId);
    const to = reordered.findIndex((f) => f.id === targetId);
    if (from === -1 || to === -1) return;
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    setFamilies(reordered);
    reorderGeneric('composite-codes', reordered.map((f) => f.id));
  };

  // Genèric per a Subfamília/Producte/Model: reorder dins la llista completa
  // (filtrada per pare a la vista), i reparentar si es deixa anar sobre la
  // capçalera d'un pare diferent o sobre un germà d'un altre pare.
  function makeDropOnParent<R extends Row>(
    dragRef: React.MutableRefObject<R | null>,
    list: R[],
    setList: (r: R[]) => void,
    parentField: keyof R,
    path: string,
  ) {
    return async (parentId: number) => {
      const payload = dragRef.current; dragRef.current = null; setDragOver(null);
      if (!payload || payload[parentField] === parentId) return;
      await api.put(`/catalog/${path}/${payload.id}`, { [parentField as string]: parentId });
      fetchAll();
    };
  }
  function makeDropOnSibling<R extends Row>(
    dragRef: React.MutableRefObject<R | null>,
    list: R[],
    setList: (r: R[]) => void,
    parentField: keyof R,
    path: string,
  ) {
    return async (target: R) => {
      const payload = dragRef.current; dragRef.current = null; setDragOver(null);
      if (!payload || payload.id === target.id) return;
      const reordered = [...list];
      const from = reordered.findIndex((it) => it.id === payload.id);
      const to = reordered.findIndex((it) => it.id === target.id);
      if (from === -1 || to === -1) return;
      const [moved] = reordered.splice(from, 1);
      const reparented = moved[parentField] !== target[parentField];
      (moved as any)[parentField] = target[parentField];
      reordered.splice(to, 0, moved);
      setList(reordered);
      try {
        if (reparented) await api.put(`/catalog/${path}/${payload.id}`, { [parentField as string]: target[parentField] });
        await api.put(`/catalog/${path}/reorder`, { ids: reordered.map((it) => it.id) });
      } catch { setError('Error en desar l\'ordre'); fetchAll(); }
    };
  }

  const subDropOnFamily = makeDropOnParent(draggingSub, subfamilies, setSubfamilies, 'compositeCodeId', 'composite-subfamilies');
  const subDropOnSibling = makeDropOnSibling(draggingSub, subfamilies, setSubfamilies, 'compositeCodeId', 'composite-subfamilies');
  const prodDropOnSubfamily = makeDropOnParent(draggingProd, products, setProducts, 'subfamilyId', 'composite-products');
  const prodDropOnSibling = makeDropOnSibling(draggingProd, products, setProducts, 'subfamilyId', 'composite-products');
  const modelDropOnProduct = makeDropOnParent(draggingModel, models, setModels, 'productId', 'composite-models');
  const modelDropOnSibling = makeDropOnSibling(draggingModel, models, setModels, 'productId', 'composite-models');
  const variantDropOnModel = makeDropOnParent(draggingVariant, variants, setVariants, 'modelId', 'composite-variants');
  const variantDropOnSibling = makeDropOnSibling(draggingVariant, variants, setVariants, 'modelId', 'composite-variants');

  // ── Cerca per text ─────────────────────────────────────────────────────────
  // Si l'usuari cerca, es calcula per a cada nivell si ell mateix o algun dels
  // seus descendents fa match (matchSets), i es propaga cap avall un "full
  // match" quan un ancestre coincideix pel seu propi nom/codi — en aquest cas
  // es mostren TOTS els seus descendents sense filtrar-los també.
  const normalize = (s?: string | null) =>
    (s ?? '').toString().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  const q = normalize(searchText.trim());
  const selfMatch = (r: Row) => normalize(r.code).includes(q) || normalize(r.name).includes(q);

  const matchSets = useMemo(() => {
    if (!q) return null;
    const variantHas = new Map<number, boolean>();
    variants.forEach((v) => variantHas.set(v.id, selfMatch(v)));

    const modelSelf = new Map<number, boolean>();
    const modelHas = new Map<number, boolean>();
    models.forEach((m) => {
      const self = selfMatch(m);
      modelSelf.set(m.id, self);
      const childMatch = variants.some((v) => v.modelId === m.id && variantHas.get(v.id));
      modelHas.set(m.id, self || childMatch);
    });

    const prodSelf = new Map<number, boolean>();
    const prodHas = new Map<number, boolean>();
    products.forEach((p) => {
      const self = selfMatch(p);
      prodSelf.set(p.id, self);
      const childMatch = models.some((m) => m.productId === p.id && modelHas.get(m.id));
      prodHas.set(p.id, self || childMatch);
    });

    const subSelf = new Map<number, boolean>();
    const subHas = new Map<number, boolean>();
    subfamilies.forEach((s) => {
      const self = selfMatch(s);
      subSelf.set(s.id, self);
      const childMatch = products.some((p) => p.subfamilyId === s.id && prodHas.get(p.id));
      subHas.set(s.id, self || childMatch);
    });

    const famSelf = new Map<number, boolean>();
    const famHas = new Map<number, boolean>();
    families.forEach((f) => {
      const self = selfMatch(f);
      famSelf.set(f.id, self);
      const childMatch = subfamilies.some((s) => s.compositeCodeId === f.id && subHas.get(s.id));
      famHas.set(f.id, self || childMatch);
    });

    return { variantHas, modelSelf, modelHas, prodSelf, prodHas, subSelf, subHas, famSelf, famHas };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, families, subfamilies, products, models, variants]);

  const visFam = (showInactive ? families : families.filter((f) => f.isActive))
    .filter((f) => !matchSets || matchSets.famHas.get(f.id));
  const subOf = (famId: number, parentFull = false) => {
    const base = (showInactive ? subfamilies : subfamilies.filter((s) => s.isActive)).filter((s) => s.compositeCodeId === famId);
    if (!matchSets || parentFull) return base;
    return base.filter((s) => matchSets.subHas.get(s.id));
  };
  const prodOf = (subId: number, parentFull = false) => {
    const base = (showInactive ? products : products.filter((p) => p.isActive)).filter((p) => p.subfamilyId === subId);
    if (!matchSets || parentFull) return base;
    return base.filter((p) => matchSets.prodHas.get(p.id));
  };
  const modelOf = (prodId: number, parentFull = false) => {
    const base = (showInactive ? models : models.filter((m) => m.isActive)).filter((m) => m.productId === prodId);
    if (!matchSets || parentFull) return base;
    return base.filter((m) => matchSets.modelHas.get(m.id));
  };
  const variantOf = (modelId: number, parentFull = false) => {
    const base = (showInactive ? variants : variants.filter((v) => v.isActive)).filter((v) => v.modelId === modelId);
    if (!matchSets || parentFull) return base;
    return base.filter((v) => matchSets.variantHas.get(v.id));
  };

  // ── Exportació a Excel ──────────────────────────────────────────────────────
  // Exporta exactament l'arbre que es veu en pantalla (mateix filtre de cerca
  // i de "mostra inactius"), aplanat a una fila per cada node fulla.
  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const { default: ExcelJS } = await import('exceljs');
      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet('Tipus de producte');
      // Una columna per nivell (cascada), igual que la indentació en pantalla
      // — cada fila es un sol node (família, subfamília...) i nomes omple la
      // seva pròpia columna, deixant la resta en blanc.
      const LEVEL_COLS = ['Família', 'Subfamília', 'Producte', 'Model', 'Variant'];
      const headers = [...LEVEL_COLS, 'Actiu'];
      sheet.columns = headers.map((h) => ({ width: h === 'Actiu' ? 8 : 30 }));
      const headerRow = sheet.addRow(headers);
      headerRow.font = { bold: true };
      headerRow.eachCell((cell) => { cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE2E8F0' } }; });

      const addNodeRow = (level: number, code: string, name: string, isActive: number, bold = false) => {
        const values: string[] = new Array(headers.length).fill('');
        values[level] = name ? `${code} - ${name}` : code;
        values[headers.length - 1] = isActive ? 'Sí' : 'No';
        const row = sheet.addRow(values);
        if (bold) row.font = { bold: true };
      };

      visFam.forEach((fam) => {
        addNodeRow(0, fam.code, fam.name, fam.isActive, true);
        const famFull = !!matchSets?.famSelf.get(fam.id);
        const subs = subOf(fam.id, famFull);
        subs.forEach((sub) => {
          addNodeRow(1, sub.code, sub.name, sub.isActive);
          const subFull = famFull || !!matchSets?.subSelf.get(sub.id);
          const prods = prodOf(sub.id, subFull);
          prods.forEach((prod) => {
            addNodeRow(2, prod.code, prod.name, prod.isActive);
            const prodFull = subFull || !!matchSets?.prodSelf.get(prod.id);
            const mods = modelOf(prod.id, prodFull);
            mods.forEach((model) => {
              addNodeRow(3, model.code, model.name || '', model.isActive);
              const modelFull = prodFull || !!matchSets?.modelSelf.get(model.id);
              const vars = variantOf(model.id, modelFull);
              vars.forEach((variant) => {
                addNodeRow(4, variant.code, variant.name || '', variant.isActive);
              });
            });
          });
        });
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'tipus-de-producte.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError('Error en exportar a Excel');
    } finally {
      setExporting(false);
    }
  };

  const rowCls = 'flex items-center gap-2 py-1.5';
  const dragHandle = <span className="text-slate-300 cursor-grab active:cursor-grabbing select-none">⠿</span>;
  const iconBtnCls = 'p-1 hover:bg-muted rounded';

  if (loading) return <p className="text-sm text-muted-foreground text-center py-8">Carregant...</p>;

  return (
    <div className="space-y-2">
      {error && (
        <div className="rounded border border-red-300 bg-red-50 px-4 py-2 text-sm text-red-700 flex justify-between">
          {error}
          <button onClick={() => setError(null)}><X className="h-4 w-4" /></button>
        </div>
      )}

      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="h-3.5 w-3.5 text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Cercar per codi o nom..."
            className="h-8 text-xs pl-8 pr-7"
          />
          {searchText && (
            <button
              onClick={() => setSearchText('')}
              title="Netejar cerca"
              className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-muted"
            >
              <FilterX className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
          )}
        </div>
        <Button size="sm" variant="outline" onClick={handleExportExcel} disabled={exporting}>
          <FileDown className="h-3.5 w-3.5 mr-1" /> {exporting ? 'Exportant...' : 'Exportar a Excel'}
        </Button>
      </div>

      {q && visFam.length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-6">Cap resultat per a "{searchText.trim()}".</p>
      )}

      {visFam.map((fam) => {
        const famFull = !!matchSets?.famSelf.get(fam.id);
        const famOpen = q ? true : expandedFam.has(fam.id);
        const subs = subOf(fam.id, famFull);
        return (
          <div key={fam.id} className="rounded-xl border bg-card overflow-hidden">
            <div
              draggable={famEditId !== fam.id}
              onDragStart={() => { draggingFam.current = fam.id; }}
              onDragOver={(e) => { e.preventDefault(); setDragOver({ level: 'fam', id: fam.id }); }}
              onDrop={() => { if (draggingSub.current) subDropOnFamily(fam.id); else famDrop(fam.id); }}
              onDragEnd={() => { draggingFam.current = null; setDragOver(null); }}
              className={`${rowCls} px-3 ${!fam.isActive ? 'opacity-50' : ''} ${dragOver?.level === 'fam' && dragOver.id === fam.id ? 'bg-blue-50' : 'bg-muted/40'}`}
            >
              {dragHandle}
              <button onClick={() => toggle(setExpandedFam, fam.id)} className="p-0.5 rounded hover:bg-muted">
                {famOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </button>
              {famEditId === fam.id ? (
                <>
                  <InlineInput value={famEditForm.code} onChange={(v) => setFamEditForm((f) => ({ ...f, code: v }))} placeholder="CODI" className="w-20" />
                  <InlineInput value={famEditForm.name} onChange={(v) => setFamEditForm((f) => ({ ...f, name: v }))} placeholder="Nom" className="flex-1" />
                  <button onClick={() => { updateGeneric('composite-codes', fam.id, famEditForm); setFamEditId(null); }} className="p-1 hover:bg-green-100 rounded text-green-600"><Check className="h-4 w-4" /></button>
                  <button onClick={() => setFamEditId(null)} className={iconBtnCls}><X className="h-4 w-4" /></button>
                </>
              ) : (
                <>
                  <span className="font-mono text-xs text-muted-foreground w-10 shrink-0 whitespace-nowrap">{fam.code}</span>
                  <span className="font-semibold text-sm flex-1">{fam.name}</span>
                  <span className="text-xs text-muted-foreground">{subs.length} subfamíli{subs.length === 1 ? 'a' : 'es'}</span>
                  <button onClick={() => toggleActiveGeneric('composite-codes', fam)} title={fam.isActive ? 'Desactivar' : 'Activar'}>
                    {fam.isActive ? <ToggleRight className="h-5 w-5 text-green-600" /> : <ToggleLeft className="h-5 w-5 text-muted-foreground" />}
                  </button>
                  <button onClick={() => { setFamEditId(fam.id); setFamEditForm({ code: fam.code, name: fam.name }); }} className={iconBtnCls}><Edit2 className="h-4 w-4" /></button>
                  <button onClick={() => deleteGeneric('composite-codes', fam)} className="p-1 hover:bg-red-50 rounded text-red-500"><X className="h-4 w-4" /></button>
                </>
              )}
            </div>

            {famOpen && (
              <div className="border-t bg-white">
                {subs.length === 0 && subAddingFor !== fam.id && (
                  <p className="px-4 py-3 pl-10 text-xs text-muted-foreground">Sense subfamílies.</p>
                )}
                {subs.map((sub) => {
                  const subFull = famFull || !!matchSets?.subSelf.get(sub.id);
                  const subOpen = q ? true : expandedSub.has(sub.id);
                  const prods = prodOf(sub.id, subFull);
                  return (
                    <div key={sub.id} className="border-b last:border-0">
                      <div
                        draggable={subEditId !== sub.id}
                        onDragStart={() => { draggingSub.current = sub; }}
                        onDragOver={(e) => { e.preventDefault(); setDragOver({ level: 'sub', id: sub.id }); }}
                        onDrop={() => { if (draggingProd.current) prodDropOnSubfamily(sub.id); else subDropOnSibling(sub); }}
                        onDragEnd={() => { draggingSub.current = null; setDragOver(null); }}
                        className={`${rowCls} pl-10 pr-3 ${!sub.isActive ? 'opacity-50' : ''} ${dragOver?.level === 'sub' && dragOver.id === sub.id ? 'bg-blue-50' : ''}`}
                      >
                        {dragHandle}
                        <button onClick={() => toggle(setExpandedSub, sub.id)} className="p-0.5 rounded hover:bg-muted">
                          {subOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                        </button>
                        {subEditId === sub.id ? (
                          <>
                            <InlineInput value={subEditForm.code} onChange={(v) => setSubEditForm((f) => ({ ...f, code: v }))} placeholder="CODI" className="w-16" />
                            <InlineInput value={subEditForm.name} onChange={(v) => setSubEditForm((f) => ({ ...f, name: v }))} placeholder="Nom" className="flex-1" />
                            <button onClick={() => { updateGeneric('composite-subfamilies', sub.id, subEditForm); setSubEditId(null); }} className="p-1 hover:bg-green-100 rounded text-green-600"><Check className="h-4 w-4" /></button>
                            <button onClick={() => setSubEditId(null)} className={iconBtnCls}><X className="h-4 w-4" /></button>
                          </>
                        ) : (
                          <>
                            <span className="font-mono text-xs text-muted-foreground w-12 shrink-0 whitespace-nowrap">{sub.code}</span>
                            <span className="text-sm flex-1">{sub.name}</span>
                            <span className="text-[11px] text-muted-foreground">{prods.length} producte{prods.length === 1 ? '' : 's'}</span>
                            <button onClick={() => toggleActiveGeneric('composite-subfamilies', sub)} title={sub.isActive ? 'Desactivar' : 'Activar'}>
                              {sub.isActive ? <ToggleRight className="h-4 w-4 text-green-600" /> : <ToggleLeft className="h-4 w-4 text-muted-foreground" />}
                            </button>
                            <button onClick={() => { setSubEditId(sub.id); setSubEditForm({ code: sub.code, name: sub.name }); }} className="p-1 hover:bg-muted rounded"><Edit2 className="h-3.5 w-3.5" /></button>
                            <button onClick={() => deleteGeneric('composite-subfamilies', sub)} className="p-1 hover:bg-red-50 rounded text-red-500"><X className="h-3.5 w-3.5" /></button>
                          </>
                        )}
                      </div>

                      {subOpen && (
                        <div className="bg-slate-50/60">
                          {prods.length === 0 && prodAddingFor !== sub.id && (
                            <p className="px-4 py-2 pl-16 text-[11px] text-muted-foreground">Sense productes.</p>
                          )}
                          {prods.map((prod) => {
                            const prodFull = subFull || !!matchSets?.prodSelf.get(prod.id);
                            const prodOpen = q ? true : expandedProd.has(prod.id);
                            const mods = modelOf(prod.id, prodFull);
                            return (
                              <div key={prod.id}>
                                <div
                                  draggable={prodEditId !== prod.id}
                                  onDragStart={() => { draggingProd.current = prod; }}
                                  onDragOver={(e) => { e.preventDefault(); setDragOver({ level: 'prod', id: prod.id }); }}
                                  onDrop={() => { if (draggingModel.current) modelDropOnProduct(prod.id); else prodDropOnSibling(prod); }}
                                  onDragEnd={() => { draggingProd.current = null; setDragOver(null); }}
                                  className={`${rowCls} pl-16 pr-3 ${!prod.isActive ? 'opacity-50' : ''} ${dragOver?.level === 'prod' && dragOver.id === prod.id ? 'bg-blue-50' : ''}`}
                                >
                                  {dragHandle}
                                  <button onClick={() => toggle(setExpandedProd, prod.id)} className="p-0.5 rounded hover:bg-muted">
                                    {prodOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                                  </button>
                                  {prodEditId === prod.id ? (
                                    <>
                                      <InlineInput value={prodEditForm.code} onChange={(v) => setProdEditForm((f) => ({ ...f, code: v }))} placeholder="CODI" className="w-14" />
                                      <InlineInput value={prodEditForm.name} onChange={(v) => setProdEditForm((f) => ({ ...f, name: v }))} placeholder="Nom" className="flex-1" />
                                      <button onClick={() => { updateGeneric('composite-products', prod.id, prodEditForm); setProdEditId(null); }} className="p-1 hover:bg-green-100 rounded text-green-600"><Check className="h-3.5 w-3.5" /></button>
                                      <button onClick={() => setProdEditId(null)} className={iconBtnCls}><X className="h-3.5 w-3.5" /></button>
                                    </>
                                  ) : (
                                    <>
                                      <span className="font-mono text-[11px] text-muted-foreground w-14 shrink-0 whitespace-nowrap">{prod.code}</span>
                                      <span className="text-xs flex-1">{prod.name}</span>
                                      <span className="text-[10px] text-muted-foreground">{mods.length} model{mods.length === 1 ? '' : 's'}</span>
                                      <button onClick={() => toggleActiveGeneric('composite-products', prod)} title={prod.isActive ? 'Desactivar' : 'Activar'}>
                                        {prod.isActive ? <ToggleRight className="h-3.5 w-3.5 text-green-600" /> : <ToggleLeft className="h-3.5 w-3.5 text-muted-foreground" />}
                                      </button>
                                      <button onClick={() => { setProdEditId(prod.id); setProdEditForm({ code: prod.code, name: prod.name }); }} className="p-1 hover:bg-muted rounded"><Edit2 className="h-3 w-3" /></button>
                                      <button onClick={() => deleteGeneric('composite-products', prod)} className="p-1 hover:bg-red-50 rounded text-red-500"><X className="h-3 w-3" /></button>
                                    </>
                                  )}
                                </div>

                                {prodOpen && (
                                  <div className="bg-slate-100/60">
                                    {mods.length === 0 && modelAddingFor !== prod.id && (
                                      <p className="px-4 py-2 pl-24 text-[10px] text-muted-foreground">Sense models.</p>
                                    )}
                                    {mods.map((model) => {
                                      const modelFull = prodFull || !!matchSets?.modelSelf.get(model.id);
                                      const modelOpen = q ? true : expandedModel.has(model.id);
                                      const vars = variantOf(model.id, modelFull);
                                      return (
                                      <div key={model.id}>
                                      <div
                                        draggable={modelEditId !== model.id}
                                        onDragStart={() => { draggingModel.current = model; }}
                                        onDragOver={(e) => { e.preventDefault(); setDragOver({ level: 'model', id: model.id }); }}
                                        onDrop={() => { if (draggingVariant.current) variantDropOnModel(model.id); else modelDropOnSibling(model); }}
                                        onDragEnd={() => { draggingModel.current = null; setDragOver(null); }}
                                        className={`${rowCls} pl-24 pr-3 ${!model.isActive ? 'opacity-50' : ''} ${dragOver?.level === 'model' && dragOver.id === model.id ? 'bg-blue-50' : ''}`}
                                      >
                                        {dragHandle}
                                        <button onClick={() => toggle(setExpandedModel, model.id)} className="p-0.5 rounded hover:bg-muted">
                                          {modelOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                                        </button>
                                        {modelEditId === model.id ? (
                                          <>
                                            <InlineInput value={modelEditForm.code} onChange={(v) => setModelEditForm((f) => ({ ...f, code: v }))} placeholder="CODI" className="w-14" />
                                            <InlineInput value={modelEditForm.name} onChange={(v) => setModelEditForm((f) => ({ ...f, name: v }))} placeholder="Nom (opcional)" className="flex-1" />
                                            <button onClick={() => { updateGeneric('composite-models', model.id, modelEditForm); setModelEditId(null); }} className="p-1 hover:bg-green-100 rounded text-green-600"><Check className="h-3.5 w-3.5" /></button>
                                            <button onClick={() => setModelEditId(null)} className={iconBtnCls}><X className="h-3.5 w-3.5" /></button>
                                          </>
                                        ) : (
                                          <>
                                            <span className="font-mono text-[11px] text-muted-foreground w-16 shrink-0 whitespace-nowrap">{model.code}</span>
                                            <span className="text-xs flex-1 text-muted-foreground">{model.name || '—'}</span>
                                            <span className="text-[10px] text-muted-foreground">{vars.length} variant{vars.length === 1 ? '' : 's'}</span>
                                            <button onClick={() => toggleActiveGeneric('composite-models', model)} title={model.isActive ? 'Desactivar' : 'Activar'}>
                                              {model.isActive ? <ToggleRight className="h-3.5 w-3.5 text-green-600" /> : <ToggleLeft className="h-3.5 w-3.5 text-muted-foreground" />}
                                            </button>
                                            <button onClick={() => { setModelEditId(model.id); setModelEditForm({ code: model.code, name: model.name || '' }); }} className="p-1 hover:bg-muted rounded"><Edit2 className="h-3 w-3" /></button>
                                            <button onClick={() => deleteGeneric('composite-models', model)} className="p-1 hover:bg-red-50 rounded text-red-500"><X className="h-3 w-3" /></button>
                                          </>
                                        )}
                                      </div>

                                      {modelOpen && (
                                        <div className="bg-slate-200/50">
                                          {vars.length === 0 && variantAddingFor !== model.id && (
                                            <p className="px-4 py-2 pl-32 text-[10px] text-muted-foreground">Sense variants.</p>
                                          )}
                                          {vars.map((variant) => (
                                            <div
                                              key={variant.id}
                                              draggable={variantEditId !== variant.id}
                                              onDragStart={() => { draggingVariant.current = variant; }}
                                              onDragOver={(e) => e.preventDefault()}
                                              onDrop={() => variantDropOnSibling(variant)}
                                              onDragEnd={() => { draggingVariant.current = null; }}
                                              className={`${rowCls} pl-32 pr-3 ${!variant.isActive ? 'opacity-50' : ''}`}
                                            >
                                              {dragHandle}
                                              {variantEditId === variant.id ? (
                                                <>
                                                  <InlineInput value={variantEditForm.code} onChange={(v) => setVariantEditForm((f) => ({ ...f, code: v }))} placeholder="CODI" className="w-14" />
                                                  <InlineInput value={variantEditForm.name} onChange={(v) => setVariantEditForm((f) => ({ ...f, name: v }))} placeholder="Nom (opcional)" className="flex-1" />
                                                  <button onClick={() => { updateGeneric('composite-variants', variant.id, variantEditForm); setVariantEditId(null); }} className="p-1 hover:bg-green-100 rounded text-green-600"><Check className="h-3.5 w-3.5" /></button>
                                                  <button onClick={() => setVariantEditId(null)} className={iconBtnCls}><X className="h-3.5 w-3.5" /></button>
                                                </>
                                              ) : (
                                                <>
                                                  <span className="font-mono text-[10px] text-muted-foreground w-20 shrink-0 whitespace-nowrap">{variant.code}</span>
                                                  <span className="text-[11px] flex-1 text-muted-foreground">{variant.name || '—'}</span>
                                                  <button onClick={() => toggleActiveGeneric('composite-variants', variant)} title={variant.isActive ? 'Desactivar' : 'Activar'}>
                                                    {variant.isActive ? <ToggleRight className="h-3 w-3 text-green-600" /> : <ToggleLeft className="h-3 w-3 text-muted-foreground" />}
                                                  </button>
                                                  <button onClick={() => { setVariantEditId(variant.id); setVariantEditForm({ code: variant.code, name: variant.name || '' }); }} className="p-1 hover:bg-muted rounded"><Edit2 className="h-3 w-3" /></button>
                                                  <button onClick={() => deleteGeneric('composite-variants', variant)} className="p-1 hover:bg-red-50 rounded text-red-500"><X className="h-3 w-3" /></button>
                                                </>
                                              )}
                                            </div>
                                          ))}
                                          {variantAddingFor === model.id ? (
                                            <div className={`${rowCls} pl-32 pr-3 bg-blue-50/40`}>
                                              <span className="w-4" />
                                              <InlineInput value={variantNewForm.code} onChange={(v) => setVariantNewForm((f) => ({ ...f, code: v }))} placeholder="CODI" className="w-14" />
                                              <InlineInput value={variantNewForm.name} onChange={(v) => setVariantNewForm((f) => ({ ...f, name: v }))} placeholder="Nom (opcional)" className="flex-1" />
                                              <button onClick={() => { createGeneric('composite-variants', variantNewForm, 'modelId', model.id); setVariantAddingFor(null); setVariantNewForm(emptyForm()); }} className="p-1 hover:bg-green-100 rounded text-green-600"><Check className="h-3.5 w-3.5" /></button>
                                              <button onClick={() => { setVariantAddingFor(null); setVariantNewForm(emptyForm()); }} className={iconBtnCls}><X className="h-3.5 w-3.5" /></button>
                                            </div>
                                          ) : (
                                            <div className="pl-32 pr-3 py-1">
                                              <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => { setVariantAddingFor(model.id); setVariantEditId(null); }}>
                                                <Plus className="h-3 w-3 mr-1" /> Nova variant
                                              </Button>
                                            </div>
                                          )}
                                        </div>
                                      )}
                                      </div>
                                      );
                                    })}
                                    {modelAddingFor === prod.id ? (
                                      <div className={`${rowCls} pl-24 pr-3 bg-blue-50/40`}>
                                        <span className="w-4" />
                                        <InlineInput value={modelNewForm.code} onChange={(v) => setModelNewForm((f) => ({ ...f, code: v }))} placeholder="CODI" className="w-14" />
                                        <InlineInput value={modelNewForm.name} onChange={(v) => setModelNewForm((f) => ({ ...f, name: v }))} placeholder="Nom (opcional)" className="flex-1" />
                                        <button onClick={() => { createGeneric('composite-models', modelNewForm, 'productId', prod.id); setModelAddingFor(null); setModelNewForm(emptyForm()); }} className="p-1 hover:bg-green-100 rounded text-green-600"><Check className="h-3.5 w-3.5" /></button>
                                        <button onClick={() => { setModelAddingFor(null); setModelNewForm(emptyForm()); }} className={iconBtnCls}><X className="h-3.5 w-3.5" /></button>
                                      </div>
                                    ) : (
                                      <div className="pl-24 pr-3 py-1">
                                        <Button size="sm" variant="ghost" className="h-6 text-[11px]" onClick={() => { setModelAddingFor(prod.id); setModelEditId(null); }}>
                                          <Plus className="h-3 w-3 mr-1" /> Nou model
                                        </Button>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                          {prodAddingFor === sub.id ? (
                            <div className={`${rowCls} pl-16 pr-3 bg-blue-50/40`}>
                              <span className="w-4" />
                              <InlineInput value={prodNewForm.code} onChange={(v) => setProdNewForm((f) => ({ ...f, code: v }))} placeholder="CODI" className="w-14" />
                              <InlineInput value={prodNewForm.name} onChange={(v) => setProdNewForm((f) => ({ ...f, name: v }))} placeholder="Nom" className="flex-1" />
                              <button onClick={() => { createGeneric('composite-products', prodNewForm, 'subfamilyId', sub.id); setProdAddingFor(null); setProdNewForm(emptyForm()); }} className="p-1 hover:bg-green-100 rounded text-green-600"><Check className="h-3.5 w-3.5" /></button>
                              <button onClick={() => { setProdAddingFor(null); setProdNewForm(emptyForm()); }} className={iconBtnCls}><X className="h-3.5 w-3.5" /></button>
                            </div>
                          ) : (
                            <div className="pl-16 pr-3 py-1">
                              <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => { setProdAddingFor(sub.id); setProdEditId(null); }}>
                                <Plus className="h-3.5 w-3.5 mr-1" /> Nou producte
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}

                {subAddingFor === fam.id ? (
                  <div className={`${rowCls} pl-10 pr-3 bg-blue-50/40`}>
                    <span className="w-4" />
                    <InlineInput value={subNewForm.code} onChange={(v) => setSubNewForm((f) => ({ ...f, code: v }))} placeholder="CODI" className="w-16" />
                    <InlineInput value={subNewForm.name} onChange={(v) => setSubNewForm((f) => ({ ...f, name: v }))} placeholder="Nom" className="flex-1" />
                    <button onClick={() => { createGeneric('composite-subfamilies', subNewForm, 'compositeCodeId', fam.id); setSubAddingFor(null); setSubNewForm(emptyForm()); }} className="p-1 hover:bg-green-100 rounded text-green-600"><Check className="h-4 w-4" /></button>
                    <button onClick={() => { setSubAddingFor(null); setSubNewForm(emptyForm()); }} className={iconBtnCls}><X className="h-4 w-4" /></button>
                  </div>
                ) : (
                  <div className="pl-10 pr-3 py-1.5">
                    <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => { setSubAddingFor(fam.id); setSubEditId(null); }}>
                      <Plus className="h-3.5 w-3.5 mr-1" /> Nova subfamília sota {fam.name}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {famAdding ? (
        <div className="rounded-xl border bg-blue-50/40 px-3 py-2 flex items-center gap-2">
          <InlineInput value={famNewForm.code} onChange={(v) => setFamNewForm((f) => ({ ...f, code: v }))} placeholder="CODI" className="w-24" />
          <InlineInput value={famNewForm.name} onChange={(v) => setFamNewForm((f) => ({ ...f, name: v }))} placeholder="Nom" className="flex-1" />
          <button onClick={async () => { if (!famNewForm.code.trim() || !famNewForm.name.trim()) return; await api.post('/catalog/composite-codes', famNewForm); setFamAdding(false); setFamNewForm(emptyForm()); fetchAll(); }} className="p-1 hover:bg-green-100 rounded text-green-600"><Check className="h-4 w-4" /></button>
          <button onClick={() => { setFamAdding(false); setFamNewForm(emptyForm()); }} className={iconBtnCls}><X className="h-4 w-4" /></button>
        </div>
      ) : (
        <Button size="sm" variant="outline" onClick={() => { setFamAdding(true); setFamEditId(null); }}>
          <Plus className="h-4 w-4 mr-1" /> Nova Família
        </Button>
      )}
    </div>
  );
}
